// src/pages/auth/ForgotPassword.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Input, Button } from "../../components/common";
import "./ForgotPassword.css";
import { auth as importedAuth } from "../../firebase";
import { RecaptchaVerifier, signInWithPhoneNumber, getAuth } from "firebase/auth";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [phone, setPhone] = useState("");

  // Ensure recaptcha container exists before initializing
  useEffect(() => {
    if (!document.getElementById("recaptcha-container")) {
      const div = document.createElement("div");
      div.id = "recaptcha-container";
      div.style.position = "absolute";
      div.style.left = "-9999px";
      document.body.appendChild(div);
    }
  }, []);

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setPhone(value);
    setError("");
  };

  // Create or reuse the RecaptchaVerifier (same as SignupPage)
  const ensureRecaptcha = async () => {
    const runtimeAuth = importedAuth ?? getAuth();
    if (!runtimeAuth) {
      throw new Error("Firebase auth not initialized. Check src/firebase.js.");
    }

    // Reuse existing verifier if valid
    if (window.recaptchaVerifier) {
      try {
        if (window.grecaptcha && typeof window.recaptchaWidgetId !== "undefined") {
          try {
            window.grecaptcha.reset(window.recaptchaWidgetId);
          } catch (_) { /* ignore */ }
        }
        if (typeof window.recaptchaVerifier.verify === "function") {
          return window.recaptchaVerifier;
        }
      } catch (e) {
        try {
          window.recaptchaVerifier.clear && window.recaptchaVerifier.clear();
        } catch (_) {}
        window.recaptchaVerifier = undefined;
      }
    }

    // Create new RecaptchaVerifier
    try {
      window.recaptchaVerifier = new RecaptchaVerifier(
        runtimeAuth,
        "recaptcha-container",
        {
          size: "invisible",
          callback: () => {
            // reCAPTCHA solved
          },
          "expired-callback": () => {
            // token expired
            try {
              if (window.grecaptcha && typeof window.recaptchaWidgetId !== "undefined") {
                window.grecaptcha.reset(window.recaptchaWidgetId);
              }
            } catch (_) {}
          }
        }
      );

      try {
        const widgetId = await window.recaptchaVerifier.render();
        window.recaptchaWidgetId = widgetId;
      } catch (renderErr) {
        console.debug("recaptcha render non-fatal:", renderErr);
      }

      return window.recaptchaVerifier;
    } catch (err) {
      console.error("Failed to initialize reCAPTCHA verifier:", err);
      throw new Error("Failed to initialize reCAPTCHA verifier. See console for details.");
    }
  };

  const handleSendOTP = async (e) => {
    e && e.preventDefault && e.preventDefault();
    setError("");

    const phoneOnly = (phone || "").replace(/\D/g, "");
    if (!/^\d{10}$/.test(phoneOnly)) {
      setError("Please enter a valid 10-digit phone number.");
      return;
    }

    // Store phone number for next page
    sessionStorage.setItem("resetPhoneNumber", phone);

    try {
      setIsOtpLoading(true);
      const appVerifier = await ensureRecaptcha();
      const runtimeAuth = importedAuth ?? getAuth();
      const fullPhone = `+91${phoneOnly}`;

      console.info("DEBUG: calling signInWithPhoneNumber for password reset with", fullPhone);

      const confirmationResult = await signInWithPhoneNumber(runtimeAuth, fullPhone, appVerifier);
      sessionStorage.setItem("firebase_verificationId", confirmationResult.verificationId);

      navigate("/verify-otp-reset", { state: { phoneNumber: fullPhone } });
    } catch (err) {
      console.error("send otp error", err);
      setError("Refresh the page and try again");
      try {
        if (window.recaptchaWidgetId && window.grecaptcha) {
          window.grecaptcha.reset(window.recaptchaWidgetId);
        }
      } catch (_) {}
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  return (
    <div className="forgot-password-root">
      {/* TOP SECTION */}
      <div className="forgot-password-top">
        <div className="forgot-password-icon">
          <i className="fas fa-lock"></i>
        </div>
        <h1 className="forgot-password-title">
          Forgot <br /> Password?
        </h1>
        <p className="forgot-password-subtitle">
          No worries, verify OTP sent <br /> on phone number then set <br /> new password
        </p>
      </div>

      {/* BOTTOM SECTION */}
      <div className="forgot-password-bottom">
        <form className="forgot-password-form" onSubmit={handleSendOTP}>
          {/* Phone Number Label */}
          <label className="forgot-password-label">Phone Number</label>

          {/* Error Message */}
          {error && <p className="forgot-password-error">{error}</p>}

          {/* Phone Number Input */}
          <Input
            type="tel"
            placeholder="Enter your Phone Number"
            className="forgot-password-input"
            value={phone}
            onChange={handlePhoneChange}
            required
          />

          {/* Send OTP Button */}
          <Button
            type="submit"
            className="forgot-password-button"
            disabled={isOtpLoading}
            style={{
              background: "linear-gradient(90deg, #89D957 0%, #C9E265 100%)",
              color: "#062540",
              fontWeight: 600,
              fontSize: "1.05rem",
              padding: "0.9rem 2rem",
              borderRadius: "1.8rem",
              border: "none",
              marginTop: "2rem",
              minHeight: "2.8rem",
              width: "100%",
              cursor: isOtpLoading ? "not-allowed" : "pointer",
            }}
          >
            {isOtpLoading ? "Sending OTP..." : "Send OTP"}
          </Button>

          {/* Back to Login Link */}
          <a
            href="#"
            className="forgot-password-back-link"
            onClick={handleBackToLogin}
            style={{ marginTop: "1rem", display: "block", textAlign: "center" }}
          >
            Back to Login
          </a>
        </form>
      </div>

      {/* Hidden reCAPTCHA container */}
      <div id="recaptcha-container"></div>
    </div>
  );
}
